import React, { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { fmtDop } from "../helpers";
// Bundle item prices come from public.products.price (already DOP).
// Render via fmtDop — never `fp`, which would multiply by 58.5 again.

/**
 * BundlesManager — Admin tab "Bundles".
 * CRUD over public.bundles + public.bundle_items.
 * Realtime keeps the home FeaturedBundles strip in sync.
 * Picker search is debounced + paginated via backend for large catalogs.
 */

type Bundle = {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  image_url: string | null;
  discount_pct: number;
  is_featured: boolean;
  is_active: boolean;
  sort_position: number;
};

type Item = {
  id: string;
  bundle_id: string;
  product_id: string;
  quantity: number;
  sort_position: number;
};

type Product = {
  id: string;
  name: string;
  brand: string | null;
  price: number;
  image_url: string | null;
};

type Props = { lang: "es" | "en"; toast?: (msg: string, type?: string) => void };

const input: React.CSSProperties = {
  padding: "8px 10px", borderRadius: 8, border: "1px solid var(--bd2)",
  background: "var(--bg2)", color: "var(--tx)", fontSize: 13, width: "100%",
};

const btn = (kind: "primary" | "ghost" | "danger" = "primary"): React.CSSProperties => ({
  padding: "8px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700,
  cursor: "pointer",
  border: kind === "ghost" ? "1px solid var(--bd2)" : "none",
  background: kind === "primary" ? "var(--ac)" : kind === "danger" ? "#dc2626" : "transparent",
  color: kind === "ghost" ? "var(--tx)" : "#fff",
});

const card: React.CSSProperties = {
  background: "var(--bg2)", border: "1px solid var(--bd2)",
  borderRadius: 10, padding: 12,
};

const slugify = (s: string) =>
  s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);

const emptyDraft = (): Partial<Bundle> => ({
  slug: "", title: "", subtitle: "", image_url: "",
  discount_pct: 10, is_featured: true, is_active: true, sort_position: 0,
});

const PAGE_SIZE = 20;

function useDebouncedValue<T>(value: T, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export function BundlesManager({ lang, toast }: Props) {
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Partial<Bundle>>(emptyDraft());
  const [creating, setCreating] = useState(false);

  const [pickProduct, setPickProduct] = useState<Record<string, string>>({});
  const [pickQty, setPickQty] = useState<Record<string, string>>({});
  const [productQuery, setProductQuery] = useState("");

  // Bulk picker modal state
  const [pickerBundleId, setPickerBundleId] = useState<string | null>(null);
  const [pickerQuery, setPickerQuery] = useState("");
  const [pickerSelected, setPickerSelected] = useState<Record<string, number>>({});
  const [pickerSaving, setPickerSaving] = useState(false);

  // Pagination + debounce for picker catalog search
  const pickerDebouncedQuery = useDebouncedValue(pickerQuery, 250);
  const [pickerPage, setPickerPage] = useState(0);
  const [pickerResults, setPickerResults] = useState<Product[]>([]);
  const [pickerTotal, setPickerTotal] = useState(0);
  const [pickerLoading, setPickerLoading] = useState(false);

  // Keep a merged map of all known products (initial load + picker pages) for previews
  const [pickerProducts, setPickerProducts] = useState<Record<string, Product>>({});

  const T = {
    title: lang === "es" ? "Paquetes (Bundles)" : "Bundles",
    hint: lang === "es"
      ? "Crea, edita y desactiva paquetes promocionales. Los activos y destacados aparecen en Home."
      : "Create, edit and deactivate promotional bundles. Active & featured ones show up on Home.",
    new: lang === "es" ? "Nuevo paquete" : "New bundle",
    save: lang === "es" ? "Guardar" : "Save",
    cancel: lang === "es" ? "Cancelar" : "Cancel",
    edit: lang === "es" ? "Editar" : "Edit",
    activate: lang === "es" ? "Activar" : "Activate",
    deactivate: lang === "es" ? "Desactivar" : "Deactivate",
    delete: lang === "es" ? "Eliminar" : "Delete",
    deleteConfirm: lang === "es" ? "¿Eliminar este paquete y sus items?" : "Delete this bundle and its items?",
    featured: lang === "es" ? "Destacado en Home" : "Featured on Home",
    active: lang === "es" ? "Activo" : "Active",
    slug: "Slug",
    bundleTitle: lang === "es" ? "Título" : "Title",
    subtitle: lang === "es" ? "Subtítulo" : "Subtitle",
    image: lang === "es" ? "URL de imagen" : "Image URL",
    discount: lang === "es" ? "Descuento %" : "Discount %",
    sortPos: lang === "es" ? "Orden" : "Sort",
    items: lang === "es" ? "Productos del paquete" : "Bundle items",
    addItem: lang === "es" ? "Añadir producto" : "Add product",
    qty: lang === "es" ? "Cant." : "Qty",
    remove: lang === "es" ? "Quitar" : "Remove",
    base: lang === "es" ? "Precio base" : "Base price",
    final: lang === "es" ? "Precio final" : "Final price",
    pickPlaceholder: lang === "es" ? "Buscar producto…" : "Search product…",
    empty: lang === "es" ? "Sin paquetes todavía." : "No bundles yet.",
    noItems: lang === "es" ? "Sin productos. Añade al menos uno." : "No items. Add at least one.",
    bulkAdd: lang === "es" ? "Añadir varios productos" : "Add multiple products",
    bulkTitle: lang === "es" ? "Añadir productos al paquete" : "Add products to bundle",
    bulkSearch: lang === "es" ? "Buscar por nombre o marca…" : "Search by name or brand…",
    bulkSelected: lang === "es" ? "seleccionados" : "selected",
    bulkAddAll: lang === "es" ? "Añadir selección" : "Add selection",
    bulkAlready: lang === "es" ? "ya en el paquete" : "already in bundle",
    bulkNone: lang === "es" ? "Sin resultados" : "No results",
    preview: lang === "es" ? "Vista previa del paquete" : "Bundle preview",
    previewDiscount: lang === "es" ? "Descuento" : "Discount",
    prevPage: lang === "es" ? "Anterior" : "Previous",
    nextPage: lang === "es" ? "Siguiente" : "Next",
    pageOf: lang === "es" ? "de" : "of",
  };

  const fetchAll = async () => {
    const [b, i, p] = await Promise.all([
      supabase.from("bundles").select("*").order("sort_position").order("created_at", { ascending: false }),
      supabase.from("bundle_items").select("*").order("sort_position"),
      supabase.from("products").select("id,name,brand,price,image_url").eq("is_active", true).order("name"),
    ]);
    if (!b.error && b.data) setBundles(b.data as any);
    if (!i.error && i.data) setItems(i.data as any);
    if (!p.error && p.data) {
      const prods = p.data as Product[];
      setProducts(prods);
      const map: Record<string, Product> = {};
      prods.forEach(pr => { map[pr.id] = pr; });
      setPickerProducts(prev => ({ ...prev, ...map }));
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
    const ch = supabase
      .channel("admin_bundles_mgr")
      .on("postgres_changes", { event: "*", schema: "public", table: "bundles" }, () => fetchAll())
      .on("postgres_changes", { event: "*", schema: "public", table: "bundle_items" }, () => fetchAll())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  const productById = useMemo(() => {
    const m = new Map<string, Product>();
    products.forEach(p => m.set(p.id, p));
    return m;
  }, [products]);

  const itemsByBundle = useMemo(() => {
    const m = new Map<string, Item[]>();
    items.forEach(it => {
      if (!m.has(it.bundle_id)) m.set(it.bundle_id, []);
      m.get(it.bundle_id)!.push(it);
    });
    return m;
  }, [items]);

  const filteredProducts = useMemo(() => {
    const q = productQuery.trim().toLowerCase();
    if (!q) return products.slice(0, 30);
    return products.filter(p =>
      p.name.toLowerCase().includes(q) || (p.brand || "").toLowerCase().includes(q)
    ).slice(0, 30);
  }, [products, productQuery]);

  // Debounced paginated backend search for the picker modal
  useEffect(() => {
    if (!pickerBundleId) return;
    setPickerLoading(true);
    const q = pickerDebouncedQuery.trim();
    const from = pickerPage * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;

    let query = supabase
      .from("products")
      .select("id,name,brand,price,image_url", { count: "exact" })
      .eq("is_active", true)
      .order("name")
      .range(from, to);

    if (q) {
      const safe = q.replace(/%/g, "\\%").replace(/_/g, "\\_");
      query = query.or(`name.ilike.%${safe}%,brand.ilike.%${safe}%`);
    }

    query.then(({ data, count, error }) => {
      if (error) {
        setPickerLoading(false);
        return;
      }
      const rows = (data as Product[]) || [];
      setPickerResults(rows);
      setPickerTotal(count || 0);
      setPickerProducts(prev => {
        const next = { ...prev };
        rows.forEach(r => { next[r.id] = r; });
        return next;
      });
      setPickerLoading(false);
    });
  }, [pickerDebouncedQuery, pickerPage, pickerBundleId]);

  const startCreate = () => {
    setEditingId(null);
    setDraft(emptyDraft());
    setCreating(true);
  };

  const startEdit = (b: Bundle) => {
    setCreating(false);
    setEditingId(b.id);
    setDraft({ ...b });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setCreating(false);
    setDraft(emptyDraft());
  };

  const saveDraft = async () => {
    const title = (draft.title || "").trim();
    if (!title) {
      toast?.(lang === "es" ? "Falta el título" : "Title is required", "error");
      return;
    }
    const slug = (draft.slug || "").trim() || slugify(title);
    const pct = Number(draft.discount_pct ?? 0);
    if (Number.isNaN(pct) || pct < 0 || pct > 90) {
      toast?.(lang === "es" ? "Descuento inválido (0–90)" : "Invalid discount (0–90)", "error");
      return;
    }
    const payload = {
      slug,
      title,
      subtitle: (draft.subtitle || "").trim() || null,
      image_url: (draft.image_url || "").trim() || null,
      discount_pct: pct,
      is_featured: !!draft.is_featured,
      is_active: draft.is_active !== false,
      sort_position: Number(draft.sort_position ?? 0) || 0,
    };
    if (creating) {
      const { error } = await supabase.from("bundles").insert(payload);
      if (error) { toast?.(error.message, "error"); return; }
      toast?.(lang === "es" ? "Paquete creado" : "Bundle created", "success");
    } else if (editingId) {
      const { error } = await supabase.from("bundles").update(payload).eq("id", editingId);
      if (error) { toast?.(error.message, "error"); return; }
      toast?.(lang === "es" ? "Paquete actualizado" : "Bundle updated", "success");
    }
    cancelEdit();
  };

  const toggleActive = async (b: Bundle) => {
    const { error } = await supabase.from("bundles").update({ is_active: !b.is_active }).eq("id", b.id);
    if (error) toast?.(error.message, "error");
  };

  const removeBundle = async (b: Bundle) => {
    if (!confirm(T.deleteConfirm)) return;
    const { error } = await supabase.from("bundles").delete().eq("id", b.id);
    if (error) toast?.(error.message, "error");
    else toast?.(lang === "es" ? "Paquete eliminado" : "Bundle deleted", "success");
  };

  const addItem = async (bundleId: string) => {
    const pid = pickProduct[bundleId];
    const qty = Math.max(1, Number(pickQty[bundleId] ?? 1) || 1);
    if (!pid) {
      toast?.(lang === "es" ? "Selecciona un producto" : "Select a product", "error");
      return;
    }
    const existing = (itemsByBundle.get(bundleId) || []).length;
    const { error } = await supabase.from("bundle_items").insert({
      bundle_id: bundleId, product_id: pid, quantity: qty, sort_position: existing,
    });
    if (error) toast?.(error.message, "error");
    else {
      setPickProduct(prev => ({ ...prev, [bundleId]: "" }));
      setPickQty(prev => ({ ...prev, [bundleId]: "" }));
    }
  };

  const updateItemQty = async (it: Item, qty: number) => {
    if (qty < 1) return;
    const { error } = await supabase.from("bundle_items").update({ quantity: qty }).eq("id", it.id);
    if (error) toast?.(error.message, "error");
  };

  const removeItem = async (it: Item) => {
    const { error } = await supabase.from("bundle_items").delete().eq("id", it.id);
    if (error) toast?.(error.message, "error");
  };

  const openPicker = (bundleId: string) => {
    setPickerBundleId(bundleId);
    setPickerQuery("");
    setPickerPage(0);
    setPickerSelected({});
    setPickerResults([]);
    setPickerTotal(0);
  };
  const closePicker = () => {
    setPickerBundleId(null);
    setPickerQuery("");
    setPickerPage(0);
    setPickerSelected({});
    setPickerResults([]);
  };

  // reset page when query changes
  const prevDebouncedQueryRef = useRef(pickerDebouncedQuery);
  useEffect(() => {
    if (prevDebouncedQueryRef.current !== pickerDebouncedQuery) {
      setPickerPage(0);
      prevDebouncedQueryRef.current = pickerDebouncedQuery;
    }
  }, [pickerDebouncedQuery]);

  const togglePick = (productId: string) => {
    setPickerSelected(prev => {
      const next = { ...prev };
      if (next[productId] != null) delete next[productId];
      else next[productId] = 1;
      return next;
    });
  };
  const setPickQtyFor = (productId: string, qty: number) => {
    setPickerSelected(prev => ({ ...prev, [productId]: Math.max(1, qty || 1) }));
  };

  const bulkAddSelected = async () => {
    if (!pickerBundleId) return;
    const entries = Object.entries(pickerSelected);
    if (entries.length === 0) {
      toast?.(lang === "es" ? "Selecciona al menos un producto" : "Select at least one product", "error");
      return;
    }
    setPickerSaving(true);
    const existing = itemsByBundle.get(pickerBundleId) || [];
    const existingIds = new Set(existing.map(i => i.product_id));
    let nextPos = existing.length;
    const toInsert = entries
      .filter(([pid]) => !existingIds.has(pid))
      .map(([pid, qty]) => ({
        bundle_id: pickerBundleId,
        product_id: pid,
        quantity: Math.max(1, qty),
        sort_position: nextPos++,
      }));
    if (toInsert.length === 0) {
      setPickerSaving(false);
      toast?.(lang === "es" ? "Esos productos ya están en el paquete" : "Those products are already in the bundle", "error");
      return;
    }
    const { error } = await supabase.from("bundle_items").insert(toInsert);
    setPickerSaving(false);
    if (error) { toast?.(error.message, "error"); return; }
    const skipped = entries.length - toInsert.length;
    toast?.(
      lang === "es"
        ? `Añadidos ${toInsert.length}${skipped ? ` (${skipped} ya estaban)` : ""}`
        : `Added ${toInsert.length}${skipped ? ` (${skipped} already present)` : ""}`,
      "success"
    );
    closePicker();
  };

  const bundleTotals = (bundleId: string, discountPct: number) => {
    const list = itemsByBundle.get(bundleId) || [];
    let base = 0;
    list.forEach(it => {
      const p = productById.get(it.product_id);
      if (p) base += Number(p.price) * it.quantity;
    });
    const final = base * (1 - (discountPct || 0) / 100);
    return { base, final };
  };

  const renderEditor = (existing?: Bundle) => (
    <div style={{ ...card, marginBottom: 12, borderColor: "var(--ac)" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 10 }}>
        <label style={{ fontSize: 11, color: "var(--tx3)" }}>
          {T.bundleTitle}
          <input style={input} value={draft.title || ""} onChange={e => setDraft({ ...draft, title: e.target.value })} />
        </label>
        <label style={{ fontSize: 11, color: "var(--tx3)" }}>
          {T.slug}
          <input style={input} value={draft.slug || ""} placeholder={slugify(draft.title || "")}
            onChange={e => setDraft({ ...draft, slug: e.target.value })} />
        </label>
        <label style={{ fontSize: 11, color: "var(--tx3)" }}>
          {T.subtitle}
          <input style={input} value={draft.subtitle || ""} onChange={e => setDraft({ ...draft, subtitle: e.target.value })} />
        </label>
        <label style={{ fontSize: 11, color: "var(--tx3)" }}>
          {T.image}
          <input style={input} value={draft.image_url || ""} onChange={e => setDraft({ ...draft, image_url: e.target.value })} />
        </label>
        <label style={{ fontSize: 11, color: "var(--tx3)" }}>
          {T.discount}
          <input style={input} type="number" min={0} max={90} step="0.5"
            value={String(draft.discount_pct ?? 0)}
            onChange={e => setDraft({ ...draft, discount_pct: Number(e.target.value) })} />
        </label>
        <label style={{ fontSize: 11, color: "var(--tx3)" }}>
          {T.sortPos}
          <input style={input} type="number" value={String(draft.sort_position ?? 0)}
            onChange={e => setDraft({ ...draft, sort_position: Number(e.target.value) })} />
        </label>
      </div>
      <div style={{ display: "flex", gap: 14, marginTop: 10, flexWrap: "wrap" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--tx)" }}>
          <input type="checkbox" checked={!!draft.is_featured}
            onChange={e => setDraft({ ...draft, is_featured: e.target.checked })} />
          {T.featured}
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--tx)" }}>
          <input type="checkbox" checked={draft.is_active !== false}
            onChange={e => setDraft({ ...draft, is_active: e.target.checked })} />
          {T.active}
        </label>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
        <button style={btn("ghost")} onClick={cancelEdit}>{T.cancel}</button>
        <button style={btn("primary")} onClick={saveDraft}>{T.save}</button>
      </div>
    </div>
  );

  const totalPages = Math.max(1, Math.ceil(pickerTotal / PAGE_SIZE));
  const pageStart = pickerPage * PAGE_SIZE + 1;
  const pageEnd = Math.min((pickerPage + 1) * PAGE_SIZE, pickerTotal);

  return (
    <div style={{ padding: 4 }}>
      <div style={{ marginBottom: 12, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h3 style={{ margin: 0, color: "var(--tx)" }}>{T.title}</h3>
          <p style={{ margin: "4px 0 0", color: "var(--tx3)", fontSize: 12 }}>{T.hint}</p>
        </div>
        {!creating && !editingId && (
          <button style={btn("primary")} onClick={startCreate}>+ {T.new}</button>
        )}
      </div>

      {creating && renderEditor()}

      {loading ? (
        <div style={{ padding: 20, color: "var(--tx3)" }}>…</div>
      ) : bundles.length === 0 && !creating ? (
        <div style={{ padding: 20, color: "var(--tx3)" }}>{T.empty}</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {bundles.map(b => {
            const list = itemsByBundle.get(b.id) || [];
            const { base, final } = bundleTotals(b.id, b.discount_pct);
            const isEditing = editingId === b.id;
            if (isEditing) return <div key={b.id}>{renderEditor(b)}</div>;
            return (
              <div key={b.id} style={{ ...card, opacity: b.is_active ? 1 : 0.55 }}>
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <div style={{ width: 56, height: 56, borderRadius: 8, background: "var(--bg)", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {b.image_url ? <img src={b.image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 22 }}>🎁</span>}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "var(--tx)" }}>
                      {b.title}
                      {b.is_featured && <span style={{ marginLeft: 8, fontSize: 10, padding: "2px 6px", borderRadius: 6, background: "var(--ac)", color: "#fff" }}>★ {T.featured}</span>}
                      {!b.is_active && <span style={{ marginLeft: 8, fontSize: 10, padding: "2px 6px", borderRadius: 6, background: "var(--bd2)", color: "var(--tx)" }}>{lang === "es" ? "Inactivo" : "Inactive"}</span>}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--tx3)" }}>
                      {b.subtitle || b.slug} · -{Number(b.discount_pct).toFixed(0)}%
                    </div>
                    <div style={{ fontSize: 11, color: "var(--tx3)", marginTop: 2 }}>
                      {T.base}: <s>{fmtDop(base)}</s> · {T.final}: <strong style={{ color: "var(--ac)" }}>{fmtDop(final)}</strong>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <button style={btn("ghost")} onClick={() => startEdit(b)}>{T.edit}</button>
                    <button style={btn("ghost")} onClick={() => toggleActive(b)}>{b.is_active ? T.deactivate : T.activate}</button>
                    <button style={btn("danger")} onClick={() => removeBundle(b)}>{T.delete}</button>
                  </div>
                </div>

                <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px dashed var(--bd2)" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "var(--tx)", marginBottom: 6 }}>{T.items}</div>
                  {list.length === 0 ? (
                    <div style={{ fontSize: 12, color: "var(--tx3)", marginBottom: 8 }}>{T.noItems}</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 }}>
                      {list.map(it => {
                        const p = productById.get(it.product_id);
                        return (
                          <div key={it.id} style={{ display: "grid", gridTemplateColumns: "32px 1fr 80px auto auto", gap: 8, alignItems: "center" }}>
                            <div style={{ width: 32, height: 32, borderRadius: 6, background: "var(--bg)", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              {p?.image_url ? <img src={p.image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} /> : <span>📦</span>}
                            </div>
                            <div style={{ minWidth: 0, fontSize: 12, color: "var(--tx)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {p ? p.name : it.product_id.slice(0, 8)}
                              {p && <span style={{ color: "var(--tx3)" }}> · {fmtDop(p.price)}</span>}
                            </div>
                            <input
                              style={{ ...input, width: 70 }}
                              type="number" min={1} value={it.quantity}
                              onChange={e => updateItemQty(it, Number(e.target.value))}
                            />
                            <div style={{ fontSize: 11, color: "var(--tx3)", minWidth: 60, textAlign: "right" }}>
                              {p ? fmtDop(Number(p.price) * it.quantity) : "—"}
                            </div>
                            <button style={btn("danger")} onClick={() => removeItem(it)}>{T.remove}</button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <button style={btn("primary")} onClick={() => openPicker(b.id)}>
                      + {T.bulkAdd}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {pickerBundleId && (() => {
        const bundle = bundles.find(x => x.id === pickerBundleId);
        const existing = itemsByBundle.get(pickerBundleId) || [];
        const existingIds = new Set(existing.map(i => i.product_id));
        const selectedCount = Object.keys(pickerSelected).length;
        return (
          <div
            onClick={closePicker}
            style={{
              position: "fixed", inset: 0, background: "rgba(0,0,0,.55)",
              display: "flex", alignItems: "center", justifyContent: "center",
              zIndex: 1000, padding: 16,
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                width: "min(680px, 100%)", maxHeight: "85vh",
                background: "var(--bg)", border: "1px solid var(--bd2)",
                borderRadius: 12, display: "flex", flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <div style={{ padding: 14, borderBottom: "1px solid var(--bd2)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "var(--tx)" }}>{T.bulkTitle}</div>
                    <div style={{ fontSize: 11, color: "var(--tx3)" }}>{bundle?.title}</div>
                  </div>
                  <button style={btn("ghost")} onClick={closePicker}>✕</button>
                </div>
                <input
                  autoFocus
                  style={{ ...input, marginTop: 10 }}
                  placeholder={T.bulkSearch}
                  value={pickerQuery}
                  onChange={e => setPickerQuery(e.target.value)}
                />
              </div>

              <div style={{ flex: 1, overflow: "auto", padding: 8 }}>
                {pickerLoading ? (
                  <div style={{ padding: 20, color: "var(--tx3)", textAlign: "center" }}>…</div>
                ) : pickerResults.length === 0 ? (
                  <div style={{ padding: 20, color: "var(--tx3)", textAlign: "center" }}>{T.bulkNone}</div>
                ) : pickerResults.map(p => {
                  const already = existingIds.has(p.id);
                  const checked = pickerSelected[p.id] != null;
                  const qty = pickerSelected[p.id] ?? 1;
                  return (
                    <label
                      key={p.id}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "20px 32px 1fr auto 80px",
                        gap: 8, alignItems: "center",
                        padding: "8px 6px", borderRadius: 8,
                        cursor: already ? "not-allowed" : "pointer",
                        opacity: already ? 0.5 : 1,
                        background: checked ? "var(--bg2)" : "transparent",
                      }}
                    >
                      <input
                        type="checkbox"
                        disabled={already}
                        checked={checked}
                        onChange={() => togglePick(p.id)}
                      />
                      <div style={{ width: 32, height: 32, borderRadius: 6, background: "var(--bg2)", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {p.image_url ? <img src={p.image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} /> : <span>📦</span>}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--tx)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {p.name}
                          {already && <span style={{ marginLeft: 6, fontSize: 10, color: "var(--tx3)" }}>· {T.bulkAlready}</span>}
                        </div>
                        <div style={{ fontSize: 10, color: "var(--tx3)" }}>
                          {p.brand || "—"} · {fmtDop(p.price)}
                        </div>
                      </div>
                      <div style={{ fontSize: 11, color: "var(--tx3)" }}>{T.qty}</div>
                      <input
                        type="number" min={1} value={qty}
                        disabled={!checked || already}
                        onClick={e => e.preventDefault()}
                        onChange={e => setPickQtyFor(p.id, Number(e.target.value))}
                        style={{ ...input, width: 70, padding: "6px 8px" }}
                      />
                    </label>
                  );
                })}
              </div>

              {/* Pagination controls */}
              {pickerTotal > PAGE_SIZE && (
                <div style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "8px 14px", borderTop: "1px solid var(--bd2)",
                }}>
                  <button
                    style={{ ...btn("ghost"), opacity: pickerPage === 0 ? 0.4 : 1, pointerEvents: pickerPage === 0 ? "none" : "auto" }}
                    onClick={() => setPickerPage(p => Math.max(0, p - 1))}
                  >
                    ← {T.prevPage}
                  </button>
                  <div style={{ fontSize: 12, color: "var(--tx3)" }}>
                    {pageStart}–{pageEnd} {T.pageOf} {pickerTotal} · {lang === "es" ? "Página" : "Page"} {pickerPage + 1} {T.pageOf} {totalPages}
                  </div>
                  <button
                    style={{ ...btn("ghost"), opacity: pickerPage >= totalPages - 1 ? 0.4 : 1, pointerEvents: pickerPage >= totalPages - 1 ? "none" : "auto" }}
                    onClick={() => setPickerPage(p => Math.min(totalPages - 1, p + 1))}
                  >
                    {T.nextPage} →
                  </button>
                </div>
              )}

              {(() => {
                if (!bundle) return null;
                const existingItems = itemsByBundle.get(pickerBundleId) || [];
                let base = 0;
                existingItems.forEach(it => {
                  const p = productById.get(it.product_id);
                  if (p) base += Number(p.price) * it.quantity;
                });
                Object.entries(pickerSelected).forEach(([pid, qty]) => {
                  const p = pickerProducts[pid];
                  if (p) base += Number(p.price) * qty;
                });
                const discount = base * ((bundle.discount_pct || 0) / 100);
                const final = base - discount;
                return (
                  <div style={{ padding: "10px 14px", background: "var(--bg2)", borderTop: "1px solid var(--bd2)", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                    <div style={{ fontSize: 11, color: "var(--tx3)", fontWeight: 600 }}>{T.preview}</div>
                    <div style={{ fontSize: 12, color: "var(--tx)", display: "flex", gap: 10, flexWrap: "wrap" }}>
                      <span>{T.base}: <s>{fmtDop(base)}</s></span>
                      <span>{T.previewDiscount}: -{fmtDop(discount)}</span>
                      <span>{T.final}: <strong style={{ color: "var(--ac)" }}>{fmtDop(final)}</strong></span>
                    </div>
                  </div>
                );
              })()}
              <div style={{
                padding: 12, borderTop: "1px solid var(--bd2)",
                display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8,
              }}>
                <div style={{ fontSize: 12, color: "var(--tx3)" }}>
                  {selectedCount} {T.bulkSelected}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button style={btn("ghost")} onClick={closePicker}>{T.cancel}</button>
                  <button
                    style={btn("primary")}
                    disabled={pickerSaving || selectedCount === 0}
                    onClick={bulkAddSelected}
                  >
                    {pickerSaving ? "…" : `+ ${T.bulkAddAll}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default BundlesManager;
